<!--
  Una página dibujada por su HTML.

  Ocupa todo el espacio que le dan y vive dentro del marco aislado. Lo que
  rodea a la página --el sidebar y la barra-- se dibuja fuera, para que el
  peor HTML posible solo se rompa a si mismo.
-->
<script lang="ts">
  import type { AppPerson, PageRecord, PickedBlock } from "@shared/types";
  import type PocketBase from "pocketbase";

  import type { DocLoader } from "../lib/htmlDocs";
  import type { BrokenSource } from "../lib/quickAsk";
  import HtmlFrame from "./HtmlFrame.svelte";
  import { WarnNote } from "./ui";

  let {
    page,
    appId,
    client,
    loadDoc,
    roles,
    previewRole,
    previewPerson,
    previewAnon = false,
    previewNoRole = false,
    themeKey,
    showIssues = false,
    picking = false,
    onPick,
    onPickerOff,
    onBrokenSource,
  }: {
    page: PageRecord;
    /** Aplicación a la que pertenece: la página pide sus datos al servidor por ella. */
    appId: string;
    client: PocketBase;
    /** De donde sale el HTML de esta página. Cambia según quien mire. */
    loadDoc: DocLoader;
    /** Roles de quien está mirando, para que la página sepa a quien tiene delante. */
    roles?: string[];
    /** Ver la página como la vería este rol. Solo para el dueño de la aplicación. */
    previewRole?: string;
    /**
     * Y como la vería esta persona concreta, de entre las que tienen ese rol.
     * Entera, no solo su identificador: el documento también la necesita para
     * saber a quien tiene delante. Ver `HtmlFrame`.
     */
    previewPerson?: AppPerson | null;
    /** O como la vería alguien que llega sin cuenta. */
    previewAnon?: boolean;
    /** O como la vería alguien que entró y no tiene ningún rol de la aplicación. */
    previewNoRole?: boolean;
    /** Cambia con la paleta de la aplicación, para repintar la página. */
    themeKey?: string;
    /** Contar lo que el documento hace mal. Solo para quien construye. */
    showIssues?: boolean;
    /** El cursor de selección está encendido. Solo para quien construye. */
    picking?: boolean;
    /** Se senalo un elemento del documento. */
    onPick?: (block: PickedBlock) => void;
    /** El documento salió del cursor con escape. */
    onPickerOff?: () => void;
    /**
     * La página pidió una tabla o una columna que su conexión no cubre. Solo se
     * entera quien construye: lo muestra el escenario, no esta vista.
     */
    onBrokenSource?: (broken: BrokenSource) => void;
  } = $props();

  let savesToBrowser = $state(false);
  /**
   * La página consultó quién entra y, un momento después, no enseñaba datos
   * (o enseñaba un aviso): lo que muestra depende de esa persona.
   */
  let readsViewer = $state(false);
  let frame = $state<HtmlFrame | null>(null);

  /*
   * Saludar por el nombre también consulta quién entra, y ahí no hay nada que
   * avisar; tampoco en un formulario que rellena el nombre de quien lo envía. Se mira la pantalla tras cargar: solo si quedó sin datos, o con un
   * aviso a la vista, se dice cómo verla.
   */
  let viewerTimer: ReturnType<typeof setTimeout> | undefined;
  let generation = 0;
  $effect(() => {
    void page.id;
    void page.doc;
    generation++;
    savesToBrowser = false;
    readsViewer = false;
    leaves = "";
    return () => {
      generation++;
      clearTimeout(viewerTimer);
    };
  });

  function checkViewer() {
    const mine = generation;
    clearTimeout(viewerTimer);
    // La consulta diferida solo describe este documento, nunca el siguiente.
    viewerTimer = setTimeout(async () => {
      const view = await frame?.glance().catch(() => null);
      if (!view || mine !== generation) return;
      const shows =
        view.tablas.some((rows) => rows > 0) ||
        view.graficas.some((chart) => chart.conValor) ||
        view.cifras.some((figure) => /[1-9]/.test(figure)) ||
        (view.campos ?? 0) > 0;
      readsViewer = !shows || view.vacios.length > 0;
    }, 2500);
  }
  /** Vacío: la página nunca intentó salir. `stuck`: se quedó quieta. */
  let leaves = $state<"" | "tried" | "stuck">("");
</script>

<div id="view-page" class="view-page h-full">
  <HtmlFrame
    bind:this={frame}
    fill
    doc={page.doc}
    sources={page.sources}
    {themeKey}
    {appId}
    pageId={page.id}
    {previewRole}
    {previewPerson}
    {previewAnon}
    {previewNoRole}
    {client}
    {loadDoc}
    title={page.name}
    {roles}
    onStorage={() => (savesToBrowser = true)}
    onViewerRead={showIssues ? checkViewer : undefined}
    onBrokenSource={showIssues ? onBrokenSource : undefined}
    onNavigate={({ stuck }) => (leaves = stuck ? "stuck" : leaves || "tried")}
    {picking}
    {onPick}
    {onPickerOff}
  />

  {#if showIssues && savesToBrowser}
    <WarnNote>
      Esta página guarda datos temporales en el navegador. Se perderán al salir; usa una tabla para
      conservarlos.
    </WarnNote>
  {/if}
  <!--
    Una página que enseña "lo mío" o lo de un rol se ve vacía con los ojos de
    quien construye, que no tiene fila ni rol propios. Se dice cómo verla con
    contenido en vez de dejar creer que está rota. Mirando como una persona
    concreta, o sin sesión a propósito, sobra.
  -->
  {#if showIssues && readsViewer && !previewPerson && !previewAnon}
    <WarnNote>
      Esta página muestra contenido según quien entra (sus propios datos o su rol). Tal como la
      miras ahora puede verse vacía: para comprobarla, elige un rol en la barra de dirección y
      después una persona con ese rol.
    </WarnNote>
  {/if}
  {#if showIssues && leaves}
    <WarnNote>
      {leaves === "stuck"
        ? "Esta página se va a otra dirección cada vez que se carga, seguramente por tener su propio inicio de sesión. La sesión, las personas y los roles ya los pone Planer."
        : "Esta página intentó ir a otra dirección. Se la devolvió a su sitio: la navegación la dibuja Planer alrededor."}
    </WarnNote>
  {/if}
</div>

<style>
  .view-page {
    position: relative;
  }
</style>
